import { useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabaseClient';

export type GlobalSearchResultType = 'funeral' | 'lead' | 'client' | 'place';

export interface GlobalSearchResult {
  result_id: string;
  result_type: GlobalSearchResultType;
  label: string;
  description: string | null;
  url: string;
  rank: number;
}

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 200;

/**
 * Debounced global search backed by the `global_search` Postgres RPC.
 *
 * Searches people (name / email / phone) and returns the funeral, lead, quote,
 * or standalone client they belong to, plus facilities from `places` matched on
 * name or abbreviation. Queries shorter than 2 chars return an empty result set.
 * Out-of-order responses are discarded so the latest query always wins.
 */
export function useGlobalSearch(query: string) {
  const [results, setResults] = useState<GlobalSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  // Monotonically increasing id so a slow earlier request can't clobber a
  // faster later one.
  const requestId = useRef(0);

  useEffect(() => {
    const trimmed = query.trim();

    if (trimmed.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const currentRequest = ++requestId.current;

    const timer = setTimeout(() => {
      void (async () => {
        const { data, error } = await supabase.rpc('global_search', {
          search_term: trimmed,
        });

        // A newer query superseded this one — drop the stale response.
        if (currentRequest !== requestId.current) {
          return;
        }

        if (error) {
          console.error('global_search failed:', error);
          setResults([]);
        } else {
          setResults((data as GlobalSearchResult[]) ?? []);
        }
        setLoading(false);
      })();
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query]);

  return { results, loading };
}
